// ============================================
// AUTH COMPONENTS
// ============================================

export type { DateRange } from "react-day-picker";
// ============================================
// TOAST (sonner)
// ============================================
export { toast } from "sonner";
export {
  LoginForm,
  LogoutButton,
  SignUpForm,
} from "./components/auth/index";
// ============================================
// SIDEBAR (b37)
// ============================================
export {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
} from "./components/b37/sidebar";
// ============================================
// CHARTS
// ============================================
export {
  ChartCollection,
  type ChartCollectionProps,
  ChartItem,
  type ChartItemProps,
} from "./components/charts/bar-chart";
export {
  BigNumberItem,
  type BigNumberItemProps,
} from "./components/charts/big-number";
export {
  BigNumberCollection,
  type BigNumberCollectionProps,
  collectionGridVariants,
} from "./components/charts/big-number-collection";
export {
  LineChartCollection,
  type LineChartCollectionProps,
  LineChartItem,
  type LineChartItemProps,
} from "./components/charts/line-chart";
export {
  RadialChartCollection,
  type RadialChartCollectionProps,
  RadialChartItem,
  type RadialChartItemProps,
} from "./components/charts/radial-chart";
export * from "./components/data/card";
// ============================================
// DATA
// ============================================
export {
  CardCollection,
  type CardCollectionProps,
} from "./components/data/card-collection";
export {
  DataGrid,
  type DataGridProps,
} from "./components/data/data-grid";
export {
  type ColumnDefinition,
  createDynamicColumns,
} from "./components/data/data-grid-helpers";
export {
  ListCollection,
  type ListCollectionProps,
} from "./components/data/list-collection";
export {
  type ListCollectionItem,
  ListItem,
  type ListItemProps,
} from "./components/data/list-item";
export {
  TableCollection,
  type TableCollectionProps,
} from "./components/data/table-collection";
export {
  createActionsColumn,
  createBadgeColumn,
  createCurrencyColumn,
  createDateColumn,
  createImageColumn,
  createNumberColumn,
  createSelectColumn,
  createSortableColumn,
} from "./components/data/table-helpers";
// ============================================
// DISPLAY
// ============================================
export {
  type CardCollectionItem,
  CardItem,
  type CardItemProps,
} from "./components/display/card";
export { Image as DisplayImage } from "./components/display/image";
export {
  LogoAvatar,
  type LogoAvatarProps,
  type LogoAvatarSize,
} from "./components/display/logo-avatar";
export { Text } from "./components/display/text";
export { Title } from "./components/display/title";
export {
  VideoPlayer,
  VideoPlayerContent,
  type VideoPlayerContentProps,
  VideoPlayerControlBar,
  type VideoPlayerControlBarProps,
  VideoPlayerMuteButton,
  type VideoPlayerMuteButtonProps,
  VideoPlayerPlayButton,
  type VideoPlayerPlayButtonProps,
  type VideoPlayerProps,
  VideoPlayerSeekBackwardButton,
  type VideoPlayerSeekBackwardButtonProps,
  VideoPlayerSeekForwardButton,
  type VideoPlayerSeekForwardButtonProps,
  VideoPlayerTimeDisplay,
  type VideoPlayerTimeDisplayProps,
  VideoPlayerTimeRange,
  type VideoPlayerTimeRangeProps,
  VideoPlayerVolumeRange,
  type VideoPlayerVolumeRangeProps,
} from "./components/display/video-player";
// ============================================
// FEEDBACK
// ============================================
export { Hint, hintVariants } from "./components/feedback/hint";
export {
  ProgressCollection,
  ProgressItem,
} from "./components/feedback/progress";
export { Spinner } from "./components/feedback/spinner";
// ============================================
// FORMS
// ============================================
export {
  ButtonCollection,
  type ButtonCollectionProps,
  ButtonItem,
  type ButtonItemProps,
} from "./components/forms/button";
export {
  Field,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from "./components/forms/field";
export {
  FieldItem,
  type FieldItemData,
} from "./components/forms/field-item";
export { Fields } from "./components/forms/fields";
export {
  FileUploaderItem,
  type FileUploaderItemProps,
} from "./components/forms/file-uploader";
export {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "./components/forms/form";
export {
  SignaturePadItem,
  type SignaturePadItemProps,
} from "./components/forms/signature-pad";
// ============================================
// LAYOUT
// ============================================
export { Container } from "./components/layout/container";
export { SectionHeader } from "./components/layout/section-header";
export { Separator as LayoutSeparator } from "./components/layout/separator";
export { Spacer } from "./components/layout/spacer";
export type { ActionRowItem as ActionRowItemType } from "./components/navigation/action-row";
export { ActionRowItem } from "./components/navigation/action-row";
// ============================================
// NAVIGATION COMPONENTS
// ============================================
export type {
  AppSidebarProps,
  NavGroup,
  NavItem,
} from "./components/navigation/app-sidebar";
export { AppSidebar } from "./components/navigation/app-sidebar";
export type {
  AppItem,
  AppSwitcherProps,
} from "./components/navigation/app-switcher";
export { AppSwitcher } from "./components/navigation/app-switcher";
export type { AuthNavControlsProps } from "./components/navigation/auth-nav-controls";
export { AuthNavControls } from "./components/navigation/auth-nav-controls";
export {
  Breadcrumb,
  BreadcrumbEllipsis,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "./components/navigation/breadcrumb";
export type { NavDrawerAuthFooterProps } from "./components/navigation/nav-drawer-auth-footer";
export { NavDrawerAuthFooter } from "./components/navigation/nav-drawer-auth-footer";
export type { NavbarProps } from "./components/navigation/navbar";
export { Navbar } from "./components/navigation/navbar";
export type {
  Organization,
  OrgSwitcherProps,
} from "./components/navigation/org-switcher";
export { OrgSwitcher } from "./components/navigation/org-switcher";
export type { SiteNavbarProps } from "./components/navigation/site-navbar";
export { SiteNavbar } from "./components/navigation/site-navbar";
export {
  type TabOption,
  Tabs as DraggableTabs,
  type TabsProps as DraggableTabsProps,
} from "./components/navigation/tabs";
export {
  type TabItem,
  TabsContainer,
  type TabsContainerProps,
} from "./components/navigation/tabs-container";
export { ThemeProvider } from "./components/navigation/theme-provider";
export type { UserMenuProps } from "./components/navigation/user-menu";
export { UserMenu } from "./components/navigation/user-menu";
// ============================================
// SHARED COMPONENTS
// ============================================
export type { AccessDeniedProps } from "./components/shared/access-denied";
export { AccessDenied } from "./components/shared/access-denied";
export type { AccessStateProps } from "./components/shared/access-state";
export { AccessState } from "./components/shared/access-state";
export type { FooterProps } from "./components/shared/footer";
export { Footer } from "./components/shared/footer";
export * from "./components/ui/accordion";
export * from "./components/ui/alert";
export * from "./components/ui/alert-dialog";
export { AspectRatio } from "./components/ui/aspect-ratio";
// ============================================
// UI PRIMITIVES (shadcn/ui)
// ============================================
export { Avatar, AvatarFallback, AvatarImage } from "./components/ui/avatar";
export { Badge } from "./components/ui/badge";
export * from "./components/ui/breadcrumb";
export { Button } from "./components/ui/button";
export {
  ButtonGroup,
  ButtonGroupSeparator,
  ButtonGroupText,
} from "./components/ui/button-group";
export { Calendar } from "./components/ui/calendar";
export {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "./components/ui/card";
export * from "./components/ui/carousel";
export {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartStyle,
  ChartTooltip,
  ChartTooltipContent,
} from "./components/ui/chart";
export { Checkbox } from "./components/ui/checkbox";
export {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "./components/ui/collapsible";
export {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from "./components/ui/command";
export * from "./components/ui/context-menu";
export {
  DateRangePicker,
  type DateRangePickerProps,
  type DateRangePreset,
} from "./components/ui/date-range-picker";
export {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./components/ui/dialog";
export * from "./components/ui/drawer";
export {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./components/ui/dropdown-menu";
export { FileInput } from "./components/ui/file-input";
export * from "./components/ui/form";
export * from "./components/ui/hover-card";
export { Input } from "./components/ui/input";
export { InputGroup, InputGroupAddon } from "./components/ui/input-group";
export * from "./components/ui/input-otp";
export { Label } from "./components/ui/label";
export * from "./components/ui/menubar";
export * from "./components/ui/navigation-menu";
export * from "./components/ui/pagination";
export {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
} from "./components/ui/popover";
export { Progress } from "./components/ui/progress";
export * from "./components/ui/radio-group";
export * from "./components/ui/resizable";
export { ScrollArea, ScrollBar } from "./components/ui/scroll-area";
export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "./components/ui/select";
export { Separator } from "./components/ui/separator";
export {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "./components/ui/sheet";
export { Skeleton } from "./components/ui/skeleton";
export * from "./components/ui/slider";
export * from "./components/ui/sonner";
export { Switch } from "./components/ui/switch";
export {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "./components/ui/table";
export {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "./components/ui/tabs";
export * from "./components/ui/textarea";
export { Toggle, toggleVariants } from "./components/ui/toggle";
export { ToggleGroup, ToggleGroupItem } from "./components/ui/toggle-group";
export {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./components/ui/tooltip";
// ============================================
// HOOKS
// ============================================
export { useLocalStorage } from "./hooks/use-local-storage";
// ============================================
// UTILITIES
// ============================================
export * from "./lib/app-config";
export * from "./lib/file-utils";
export * from "./lib/sidebar-state";
export * from "./lib/utils";
export * from "./lib/video-utils";
